import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { getUsers, createUser, updateUserStatus, deleteUser } from '../services/api';
import { ROLES } from '../config/rbac';

const roleBadge = r => ({
  'Super Admin': 'badge badge-green',
  'Admin':       'badge badge-blue',
  'Finance':     'badge badge-amber',
  'Developer':   'badge badge-purple',
  'Product Manager': 'badge badge-blue',
  'Support':     'badge badge-gray',
})[r] || 'badge badge-gray';

const ROLE_DESCRIPTIONS = {
  'Super Admin':     'Full access to everything',
  'Admin':           'Manage users, players, clubs, CRM, sequences',
  'Finance':         'Payments, payouts, revenue reports',
  'Developer':       'API keys, integrations, developer tools',
  'Product Manager': 'Players, clubs, marketing, pipeline',
  'Support':         'Tickets, inbox, contacts, help',
};

export default function UserManagement({ toast }) {
  const { token, auth } = useApp();
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser]   = useState({ email:'', role:'Support', password:'' });
  const [saving, setSaving]     = useState(false);

  const load = async () => {
    setLoading(true);
    try { const res = await getUsers(token); setUsers(res.data || []); }
    catch(e) { toast?.(e.message, 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [token]);

  const handleCreate = async () => {
    if (!newUser.email.trim()) { toast?.('Enter an email address', 'error'); return; }
    setSaving(true);
    try {
      const tempPassword = newUser.password.trim() || 'TransferExpo2026!';
      await createUser(token, { email: newUser.email, role: newUser.role, password: tempPassword });
      toast?.(`✅ User created — temporary password: ${tempPassword}`);

      setCreating(false);
      setNewUser({ email:'', role:'Support', password:'' });
      load();
    } catch(e) { toast?.(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (user) => {
    try {
      await updateUserStatus(token, user.id, !user.isActive);
      toast?.(`User ${user.isActive ? 'deactivated' : 'activated'}`);
      load();
    } catch(e) { toast?.(e.message, 'error'); }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete ${user.email}?`)) return;
    try {
      await deleteUser(token, user.id);
      toast?.('User deleted');
      load();
    } catch(e) { toast?.(e.message, 'error'); }
  };

  const filtered = users.filter(u => !search || u.email?.toLowerCase().includes(search.toLowerCase()) || u.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <h2 style={{ fontFamily:'var(--font-display)', fontSize:18, fontWeight:500, color:'var(--text-primary)' }}>Admin users</h2>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>+ Invite user</button>
      </div>

      {/* Role descriptions */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:20 }}>
        {ROLES.map(role => (
          <div key={role} style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:8, padding:'10px 14px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
              <span className={roleBadge(role)} style={{ fontSize:10 }}>{role}</span>
            </div>
            <div style={{ fontSize:11, color:'var(--text-dim)' }}>{ROLE_DESCRIPTIONS[role]}</div>
          </div>
        ))}
      </div>

      {/* Create user form */}
      {creating && (
        <div className="card" style={{ marginBottom:20, padding:'20px 24px' }}>
          <div style={{ fontWeight:500, fontSize:14, color:'var(--text-primary)', marginBottom:16 }}>CREATE ADMIN USER</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
            <div>
              <label style={{ fontSize:12, color:'var(--text-dim)', display:'block', marginBottom:6 }}>EMAIL</label>
              <input className="search-input" placeholder="user@transferexpo.com" value={newUser.email}
                onChange={e => setNewUser(p=>({...p, email:e.target.value}))} style={{ width:'100%' }} />
            </div>
            <div>
              <label style={{ fontSize:12, color:'var(--text-dim)', display:'block', marginBottom:6 }}>ROLE</label>
              <select value={newUser.role} onChange={e => setNewUser(p=>({...p, role:e.target.value}))}
                style={{ width:'100%', background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text-primary)', fontSize:13, padding:'8px 10px' }}>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

          </div>
          <div style={{ fontSize:12, color:'var(--text-dim)', marginBottom:12 }}>
            A temporary password <strong style={{color:'var(--accent)'}}>TransferExpo2026!</strong> will be set. User should change it on first login.
          </div>
          <div style={{ background:'var(--bg-input)', borderRadius:6, padding:'8px 12px', fontSize:12, color:'var(--text-muted)', marginBottom:14 }}>
            <strong>{newUser.role}</strong> — {ROLE_DESCRIPTIONS[newUser.role]}
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>{saving ? 'Creating…' : 'Create user'}</button>
            <button className="btn btn-ghost" onClick={() => setCreating(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Search */}
      <div style={{ display:'flex', gap:10, marginBottom:16 }}>
        <input className="search-input" placeholder="Search users by name or email…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ flex:1 }} />
        <button className="btn btn-ghost" onClick={() => setSearch('')} style={{ display: search ? 'block' : 'none' }}>Clear</button>
      </div>

      {/* Users table */}
      <div className="card" style={{ padding:0 }}>
        <div className="card-header" style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)' }}>
          <span className="card-title">ALL ADMIN USERS</span>
          <span style={{ fontSize:12, color:'var(--text-dim)' }}>{filtered.length} total</span>
        </div>
        {loading ? (
          <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>Loading users…</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>User</th><th>Email</th><th>Role</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id}>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ width:32, height:32, borderRadius:'50%', background:'rgba(167,244,50,0.15)', color:'var(--accent)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:600, flexShrink:0 }}>
                          {(u.name||u.email||'?').slice(0,2).toUpperCase()}
                        </div>
                        <span style={{ fontSize:13, fontWeight:500, color:'var(--text-primary)' }}>{u.name || u.email?.split('@')[0]}</span>
                      </div>
                    </td>
                    <td style={{ fontSize:12, color:'var(--text-dim)' }}>{u.email}</td>
                    <td><span className={roleBadge(u.role)}>{u.role || 'Admin'}</span></td>
                    <td><span className={u.isActive !== false ? 'badge badge-green' : 'badge badge-gray'}>{u.isActive !== false ? 'Active' : 'Inactive'}</span></td>
                    <td style={{ fontSize:12, color:'var(--text-dim)' }}>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</td>
                    <td>
                      <div style={{ display:'flex', gap:6 }}>
                        {u.id !== auth?.id && (
                          <>
                            <button className="btn btn-ghost" style={{ fontSize:11, padding:'3px 10px' }}
                              onClick={() => toggleStatus(u)}>
                              {u.isActive !== false ? 'Deactivate' : 'Activate'}
                            </button>
                            <button className="btn btn-ghost" style={{ fontSize:11, padding:'3px 10px', color:'var(--danger)' }}
                              onClick={() => handleDelete(u)}>
                              Delete
                            </button>
                          </>
                        )}
                        {u.id === auth?.id && <span style={{ fontSize:11, color:'var(--text-dim)' }}>You</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
